import { FaIcon, faTrash, faPlus, faArrowDownWideShort } from '@/lib/fa'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { faCaretDown } from '@fortawesome/free-solid-svg-icons'

import * as React from 'react'
import { DateField } from '@/ui/form/controls/DateField'
import { formatDate } from '@/modules/shared/utils/date'

// -----------------------------
// RepeatableSection (EXTERNAL-STATE, FULLY GENERIC)
// -----------------------------

/** Supported cell editors */
export type ColumnType =
  | 'date'
  | 'select'
  | 'price'
  | 'text'
  | 'currency'
  | 'number'

/**
 * Column config. Absolutely NO assumptions about shape of T.
 * You MUST provide how to read/write the value via `get`/`set`.
 */
export type ColumnConfig<T> = {
  order: number
  label: string
  type: ColumnType
  /** Extract a cell value from a row */
  get: (row: T) => unknown
  /** Return a NEW row with the updated cell value */
  set: (row: T, value: unknown) => T
  /** Select-specific options */
  options?: Array<{ label: string; value: string }>
  /** Number-specific props */
  min?: number
  hidden?: boolean
  readOnly?: boolean
}

export type RepeatableSectionProps<T> = {
  defaultOpen?: boolean
  title: string
  columns: Array<ColumnConfig<T>>
  /** Can user add rows */
  canAddRow?: boolean
  /** Your external data + setter */
  rows: T[]
  setRows: React.Dispatch<React.SetStateAction<T[]>>
  /** How to create a NEW empty row (required because T is arbitrary) */
  newRow: () => T
  sortRows?: (a: T, b: T) => number
  className?: string
  readOnly?: boolean
}

export function RepeatableSection<T>(props: RepeatableSectionProps<T>) {
  const {
    defaultOpen,
    title,
    columns,
    canAddRow = true,
    rows,
    sortRows,
    setRows,
    newRow,
    className,
    readOnly,
  } = props
  const sortedCols = React.useMemo(
    () => [...columns].sort((a, b) => a.order - b.order),
    [columns],
  )

  const [isOpen, setIsOpen] = React.useState(defaultOpen)
  const toggle = React.useCallback(() => setIsOpen((v) => !v), [setIsOpen])

  const addRow = React.useCallback(() => {
    if (!canAddRow) return
    setRows((prev) => [...prev, newRow()])
  }, [canAddRow, newRow, setRows])

  const handleSort = React.useCallback(() => {
    if (!sortRows) return
    setRows((prev) => {
      const next = [...prev.sort(sortRows)]
      return next
    })
  }, [sortRows, setRows])

  const removeRow = React.useCallback(
    (index: number) => {
      setRows((prev) =>
        prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
      )
    },
    [setRows],
  )

  const patchRow = React.useCallback(
    (index: number, updater: (r: T) => T) => {
      setRows((prev) => prev.map((r, i) => (i === index ? updater(r) : r)))
    },
    [setRows],
  )

  const { t } = useLocalize()
  const wrapConfirm = useWrapConfirm()

  return (
    <section
      className={`tw-w-full tw-rounded-2xl tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm ${className ?? ''}`}
    >
      <div
        className="tw-mb-3 tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') toggle()
        }}
        onClick={toggle}
        role="button"
        tabIndex={0}
      >
        <h2 className="tw-text-lg tw-font-semibold tw-tracking-tight">
          {title}
        </h2>
        <div className="tw-text-sm tw-text-gray-500">
          {rows.length}
          <FaIcon
            icon={faCaretDown}
            className={`tw-ml-1 tw-transition-transform ${isOpen ? 'tw-rotate-180' : ''}`}
          />
        </div>
      </div>

      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead>
            <tr className="tw-border-b tw-border-gray-200">
              {sortedCols.map((c) =>
                c.hidden ? null : (
                  <th
                    key={c.order}
                    className={`tw-px-2 tw-py-2 tw-text-left tw-font-medium ${
                      c.type === 'date' ||
                      c.type === 'price' ||
                      c.type === 'number'
                        ? 'tw-w-[180px]'
                        : ''
                    }`}
                  >
                    {c.label}
                  </th>
                ),
              )}
              <th className="tw-w-[1%] tw-px-2 tw-py-2"></th>
            </tr>
          </thead>
          <tbody className="tw-align-top">
            {(isOpen ? rows : rows.slice(0, 1)).map((row, index) => (
              <tr
                key={index}
                className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50"
              >
                {sortedCols.map((col) => {
                  const raw = col.get(row)

                  if (col.hidden) {
                    return null
                  }

                  if (col.type === 'date') {
                    const date =
                      raw instanceof Date ? raw : new Date(String(raw ?? ''))
                    const dateInput = isNaN(date.getTime()) ? new Date() : date
                    const iso = formatDate(dateInput, 'YYYY-MM-DD')
                    return (
                      <td
                        key={col.order}
                        className="tw-w-[180px] tw-max-w-[180px] tw-px-2 tw-py-2"
                      >
                        <DateField
                          value={iso}
                          // Data jest kluczem wiersza i po stronie serwera bywa nienullowalna
                          // (MinimumWageExpandableItemDto.DateFrom). Wyczyszczenie wysylalo
                          // "Invalid date", co wywracalo deserializacje CALEGO payloadu i konczylo
                          // sie 500. Pusty wiersz usuwa sie koszem, nie kasowaniem daty.
                          clearable={false}
                          setValue={(newDate) => {
                            patchRow(
                              index,
                              (r) => col.set(r, newDate ?? '') as T,
                            )
                          }}
                          disabled={col.readOnly}
                          openDirection="down"
                        />
                      </td>
                    )
                  }

                  if (col.type === 'select') {
                    return (
                      <td key={col.order} className="tw-px-2 tw-py-2">
                        <select
                          className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-outline-none focus:tw-border-gray-400"
                          value={String(raw ?? '')}
                          onChange={(e) =>
                            patchRow(
                              index,
                              (r) => col.set(r, e.target.value) as T,
                            )
                          }
                          disabled={col.readOnly}
                        >
                          {(col.options ?? []).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    )
                  }

                  if (col.type === 'price' || col.type === 'number') {
                    return (
                      <td
                        key={col.order}
                        className="tw-w-[180px] tw-max-w-[180px] tw-px-2 tw-py-2"
                      >
                        <input
                          className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-right tw-outline-none focus:tw-border-gray-400"
                          type="number"
                          step={col.type === 'price' ? '0.0001' : '1'}
                          min={col.min}
                          value={Number(raw ?? 0)}
                          onChange={(e) =>
                            patchRow(
                              index,
                              (r) =>
                                col.set(
                                  r,
                                  col.type === 'price'
                                    ? parseFloat(e.target.value || '0')
                                    : Number(e.target.value || 0),
                                ) as T,
                            )
                          }
                          readOnly={col.readOnly}
                          disabled={col.readOnly}
                        />
                      </td>
                    )
                  }

                  if (col.type === 'currency') {
                    return (
                      <td key={col.order} className="tw-px-2 tw-py-2">
                        <input
                          className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-uppercase tw-outline-none focus:tw-border-gray-400"
                          type="text"
                          maxLength={3}
                          value={String(raw ?? '').toUpperCase()}
                          onChange={(e) =>
                            patchRow(
                              index,
                              (r) =>
                                col.set(r, e.target.value.toUpperCase()) as T,
                            )
                          }
                          readOnly={col.readOnly}
                          disabled={col.readOnly}
                        />
                      </td>
                    )
                  }

                  // text (or fallback render)
                  return (
                    <td key={col.order} className="tw-px-2 tw-py-2">
                      <input
                        className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-outline-none focus:tw-border-gray-400"
                        type="text"
                        value={String(raw ?? '')}
                        onChange={(e) =>
                          patchRow(
                            index,
                            (r) => col.set(r, e.target.value) as T,
                          )
                        }
                        readOnly={col.readOnly}
                        disabled={col.readOnly}
                      />
                    </td>
                  )
                })}
                {!readOnly && (
                  <td className="tw-px-2 tw-py-2">
                    <button
                      type="button"
                      onClick={wrapConfirm(() => removeRow(index), {
                        message: t('configuration.delete-element-confirm'),
                      })}
                      className="tw-w-full tw-rounded-lg tw-border tw-border-red-200 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-red-600 tw-shadow-sm tw-transition hover:tw-bg-red-50"
                    >
                      <FaIcon icon={faTrash} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {!isOpen && rows.length > 1 ? (
            <tfoot>
              <tr>
                <td colSpan={4} className="tw-p-2">
                  <button
                    onClick={toggle}
                    className="tw-text-xs tw-text-blue-600 tw-transition-colors hover:tw-text-blue-800 hover:tw-underline"
                  >
                    zobacz wszystkie rekordy {`(${rows.length})`}
                  </button>
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {isOpen && !readOnly && (
        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-text-xs tw-text-gray-500">
            {canAddRow ? '' : 'Adding disabled'}
          </div>
          <div className="tw-flex tw-gap-2">
            <button
              type="button"
              onClick={addRow}
              disabled={!canAddRow}
              className="tw-inline-flex tw-select-none tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-shadow-sm tw-transition hover:tw-bg-gray-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              aria-label="Add row"
            >
              <span>
                <FaIcon icon={faPlus} />
              </span>
            </button>

            {sortRows && (
              <button
                type="button"
                onClick={handleSort}
                disabled={rows.length < 2}
                className="tw-inline-flex tw-select-none tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-shadow-sm tw-transition hover:tw-bg-gray-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                aria-label="Sort"
              >
                <span>
                  <FaIcon icon={faArrowDownWideShort} />
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
